import React, { useEffect } from 'react';
import { Box, Button, ButtonMode, CalendarIcon } from 'edenred-ui';
import { useFormContext, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { createRules, FormFields } from '@epi-forms/helpers';
import {
  maxLength,
  hasPendingLoad,
  endOfBenefitNotBeforeEntitled
} from '@epi-helpers/formHelpers/validators';
import { ITerminationDate } from '@epi-models/pages/Beneficiaries';
import { useModal } from '@epi-shared/hooks';
import { apiToDefaultDateFormat } from '@epi-helpers/dateHelper';
import { StyledTextField } from '@epi-pages/Beneficiaries/BeneficiariesListStyles';

import { TerminationDateDialog } from './components/TerminationDateDialog';
import { useTerminationDateDialog } from './hooks';

export const TerminationDate = ({
  showTerminationButton = true,
  isAddMode
}: ITerminationDate) => {
  const { t } = useTranslation();
  const { isModalOpen, openModal, closeModal } = useModal();
  const { control, watch, trigger, clearErrors } = useFormContext();
  const startDate = watch(FormFields.Entitled);
  const { handleSetDate, handleSave, endOfBenefit, disableSave } =
    useTerminationDateDialog(closeModal);
  const pendingLoadDate = watch(FormFields.PendingLoadDate);
  const {
    field: { value: EoB },
    fieldState: { error }
  } = useController({
    name: FormFields.EndOfBenefit,
    control,
    rules: createRules([
      maxLength(20),
      hasPendingLoad(pendingLoadDate),
      endOfBenefitNotBeforeEntitled
    ])
  });

  useEffect(() => {
    trigger(FormFields.EndOfBenefit);
    clearErrors(FormFields.EndOfFunds);
  }, [EoB, startDate]);

  return (
    <Box>
      {showTerminationButton && (
        <StyledTextField
          label={t('containers.beneficiaries.detail.termination_modal_title')}
          value={apiToDefaultDateFormat(EoB)}
          readOnly
          required
          error={!!error}
          helperText={error?.message}
          InputProps={{
            endAdornment: (
              <Button
                id="containers.beneficiaries.detail.end_of_benefit"
                mode={ButtonMode.Link}
                onClick={openModal}
                type="button"
              >
                <CalendarIcon />
              </Button>
            )
          }}
        />
      )}
      <TerminationDateDialog
        title={t('containers.beneficiaries.detail.termination_modal_title')}
        isOpen={isModalOpen}
        handleClose={closeModal}
        startDate={startDate}
        isAddMode={isAddMode}
        handleSetDate={handleSetDate}
        handleSave={handleSave}
        endOfBenefit={endOfBenefit}
        disableSave={disableSave}
      />
    </Box>
  );
};
